import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { Loader2, KeyRound, Settings as SettingsIcon } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function SettingsDialog({ open, onClose, family, onSaved }) {
  const [name, setName] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [calendlyUrl, setCalendlyUrl] = useState("");
  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && family) {
      setName(family.name || "");
      setWhatsappNumber(family.whatsapp_number || "");
      setCalendlyUrl(family.calendly_url || "");
      setWhatsappMessage(family.whatsapp_message || "");
      setNewPassword("");
    }
  }, [open, family]);

  const submit = async () => {
    if (!name.trim()) return toast.error("Il nome è obbligatorio");
    if (!whatsappNumber.trim()) return toast.error("Il numero WhatsApp è obbligatorio");
    if (newPassword && newPassword.length < 4) return toast.error("Password troppo corta");

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        whatsapp_number: whatsappNumber.trim(),
        calendly_url: calendlyUrl.trim(),
        whatsapp_message: whatsappMessage.trim(),
      };
      if (newPassword) payload.password = newPassword;
      const res = await api.put("/family/me", payload);
      toast.success("Impostazioni salvate");
      onSaved(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore di salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent
        data-testid="settings-dialog"
        className="max-w-md rounded-3xl border-[#E8E1D9] bg-white max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full border border-[#E8E1D9] bg-[#FBF9F6] px-3 py-1">
            <SettingsIcon className="h-3 w-3 text-[#FF7C43]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">
              Impostazioni
            </span>
          </div>
          <DialogTitle className="font-display text-2xl font-bold text-[#2D2926]">
            La tua Casa
          </DialogTitle>
          <DialogDescription className="text-[#5C5855]">
            Aggiorna i dettagli della famiglia, il messaggio ZAP e il codice di accesso.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
              Nome Casa
            </label>
            <Input
              data-testid="settings-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-xl border-[#E8E1D9] bg-white"
            />
          </div>
          <div>
            <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
              Numero WhatsApp
            </label>
            <Input
              data-testid="settings-whatsapp-input"
              value={whatsappNumber}
              onChange={(e) => setWhatsappNumber(e.target.value)}
              className="rounded-xl border-[#E8E1D9] bg-white"
            />
          </div>
          <div>
            <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
              Link Calendly
            </label>
            <Input
              data-testid="settings-calendly-input"
              value={calendlyUrl}
              onChange={(e) => setCalendlyUrl(e.target.value)}
              placeholder="https://calendly.com/..."
              className="rounded-xl border-[#E8E1D9] bg-white"
            />
          </div>
          <div>
            <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
              Messaggio ZAP predefinito
            </label>
            <Input
              data-testid="settings-message-input"
              value={whatsappMessage}
              onChange={(e) => setWhatsappMessage(e.target.value)}
              className="rounded-xl border-[#E8E1D9] bg-white"
            />
          </div>

          <Separator className="bg-[#E8E1D9]" />

          <div>
            <label className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
              <KeyRound className="h-3 w-3" /> Cambia codice (lascia vuoto per non cambiare)
            </label>
            <Input
              data-testid="settings-password-input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Nuovo codice (min 4 caratteri)"
              className="rounded-xl border-[#E8E1D9] bg-white"
            />
          </div>
        </div>

        <DialogFooter className="flex flex-row justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={saving}
            className="rounded-full border-[#E8E1D9]"
          >
            Annulla
          </Button>
          <Button
            data-testid="settings-save-button"
            type="button"
            onClick={submit}
            disabled={saving}
            className="rounded-full bg-[#FF7C43] text-white hover:bg-[#E66A35]"
          >
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
